import React, { useMemo, useState } from 'react'
import { com, kotlin } from '@eidu/web'
import { Box, Stack } from '@mui/material'
import EntityTypeField from './EntityTypeField'
import PathField, { RenderablePath } from './PathField'
import getEntityPathLabel, { transformEntityPath } from './EntityPathUtils'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import KtList = kotlin.collections.KtList
import findPathsBetweenTypes = com.eidu.sharedlib.entity.path.findPathsBetweenTypes
import EntityPath = com.eidu.sharedlib.entity.path.EntityPath

export type EntityPathPermissionProps = {
  fromEntityTypeId: EntityTypeId | undefined
  preselectedPath?: EntityPath
  entityTypes: readonly EntityType[]
  onChange: (path: EntityPath | undefined) => void
  lockedToTypeId?: EntityTypeId
}

const EntityPathPermissionContent: React.FC<EntityPathPermissionProps> = ({
  fromEntityTypeId,
  preselectedPath,
  entityTypes,
  onChange,
  lockedToTypeId,
}) => {
  const [selectedEntityType, setSelectedEntityType] = useState<EntityType>(
    lockedToTypeId
      ? entityTypes.find((type) => type.id.equals(lockedToTypeId)) ?? entityTypes[0]
      : preselectedPath
        ? entityTypes.find((type) => type.id.equals(preselectedPath.toType)) ?? entityTypes[0]
        : entityTypes[0]
  )
  const onChangeHandler = (value: EntityType) => {
    setSelectedEntityType(value)
  }
  const paths = useMemo(
    () =>
      fromEntityTypeId
        ? findPathsBetweenTypes(
            KtList.fromJsArray(entityTypes),
            fromEntityTypeId,
            selectedEntityType.id
          ).asJsReadonlyArrayView()
        : [],
    [entityTypes, fromEntityTypeId, selectedEntityType.id]
  )

  const renderablePaths: RenderablePath[] = useMemo(
    () =>
      paths
        .slice()
        .sort((a, b) => a.edges.asJsReadonlyArrayView().length - b.edges.asJsReadonlyArrayView().length)
        .map((path) => ({
          entityPath: path,
          entityPathLabel: getEntityPathLabel(entityTypes, path),
          entityPathNodes: transformEntityPath(entityTypes, path),
        })),
    [paths, entityTypes]
  )

  return (
    <Box>
      <Stack direction="column" spacing={0.5} sx={{ padding: lockedToTypeId ? 0.5 : 1 }}>
        {!lockedToTypeId && (
          <EntityTypeField
            label="Target"
            entityTypeId={selectedEntityType.id}
            types={entityTypes}
            onChange={onChangeHandler}
          />
        )}
        <PathField paths={renderablePaths} selectedPath={preselectedPath} onChange={onChange} />
      </Stack>
    </Box>
  )
}

export default EntityPathPermissionContent
